import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Filter, FilterX, Download, Trash2, Plus, Receipt, CheckCircle, Clock, XCircle, Edit, Eye, FileText, Loader2, ChevronDown } from 'lucide-react';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { exportToCSV, EXPENSE_COLUMNS } from '../utils/csvExport';
import { CustomDatePicker } from '../components/ui/custom-date-picker';
import { PaginationControls } from "../components/silica-loads/PaginationControls";
import { EditExpenseDialog } from '../components/expenses';
import { getTodayString } from '../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Compact date formatter for mobile - uses short format to prevent overflow
// Parses YYYY-MM-DD as local date to avoid timezone shift issues
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    // Parse date string manually to avoid timezone issues
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length >= 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      const dayNum = date.getDate();
      const monthStr = date.toLocaleDateString('en-IN', { month: 'short' });
      return `${dayNum} ${monthStr}`;
    }
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-IN', { month: 'short' });
    return `${day} ${month}`;
  } catch {
    return '-';
  }
};

const CATEGORIES = [
  'Material', 'Silica Purchase', 'Fuel', 'Electricity', 'Rent', 'Employee Expenses', 'Employee Celebration', 'Local Support',
  'Auditor Expenses', 'Transport From Mine to Plant', 'Transport Advance', 'Gasifier Wood/Transport', 'MESH',
  'Maintenance', 'Food', 'Marketing', 'Equipment', 'Software', 'Dozer', 'Not Sure', 'Other'
];

const STATUSES = ['pending', 'approved', 'rejected'];

// Top-level tabs shown directly (most used categories)
const TAB_CATEGORIES = ['All', 'Silica Purchase', 'Transport', 'Employee Expenses', 'Fuel', 'Rent', 'Electricity'];

// "More" dropdown: remaining categories
const MORE_CATEGORIES = [
  'Material', 'Maintenance', 'Food', 'Marketing', 'Equipment', 'Software',
  'Local Support', 'Auditor Expenses', 'Transport Advance', 'Gasifier Wood/Transport', 'MESH',
  'Infra Development', 'Dozer', 'Not Sure', 'Other'
];

// Map tab labels to actual category filter values
const TAB_TO_FILTER = {
  'All': 'all',
  'Transport': 'Transport From Mine to Plant',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const config = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle }
  };
  const { bg, text, icon: Icon } = config[status] || config.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${bg} ${text}`}>
      <Icon className="w-3.5 h-3.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export default function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all', submitted_by: '', status: 'all', start_date: '', end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('All');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const moreDropdownRef = useRef(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const pageSize = 20;
  
  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  
  // Multi-select delete state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = user.role === 'approver' || user.role === 'super_approver';
  const isSuperUser = user.role === 'super_approver';

  useEffect(() => { 
    fetchExpenses();
    fetchUsers();
    fetchEmployees();
  }, [currentPage, filters, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close "More" dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target)) {
        setShowMoreDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowMoreDropdown(false);
    setCurrentPage(1);
    const categoryFilter = TAB_TO_FILTER[tab] || (tab === 'All' ? 'all' : tab);
    setFilters(prev => ({
      ...prev,
      category: categoryFilter
    }));
  };

  const fetchExpenses = async (filterParams = null, isRetry = false) => {
    // Compute active filters up-front so both the try and catch blocks see it.
    const activeFilters = filterParams || filters;
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      // Add pagination parameters
      params.append('page', currentPage.toString());
      params.append('page_size', pageSize.toString());

      Object.keys(activeFilters).forEach(key => {
        if (activeFilters[key] && activeFilters[key] !== 'all') params.append(key, activeFilters[key]);
      });
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      const response = await axios.get(`${API}/expenses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000
      });
      // Handle paginated response
      if (response.data.items) {
        setExpenses(response.data.items);
        setTotalExpenses(response.data.total);
        setTotalPages(response.data.total_pages);
      } else {
        // Backward compatibility
        setExpenses(response.data || []);
      }
    } catch (error) {
      console.error('Expense fetch error:', error);
      if (!isRetry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        // Retry once after 2 seconds
        setTimeout(() => fetchExpenses(activeFilters, true), 2000);
        return;
      }
      // Only show error if it's not a simple empty response or cancelled request
      if (error.code !== 'ERR_CANCELED') {
        if (error.code === 'ERR_NETWORK') {
          toast.error('Network error - check your connection');
        } else if (error.response?.status >= 500) {
          toast.error('Server temporarily unavailable');
        } else if (error.response?.status !== 200) {
          // Don't show error for successful but empty responses
          toast.error('Failed to load expenses');
        }
      }
      setExpenses([]); // Set empty array on error so UI doesn't break
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const handleFilterApply = () => {
    const activeFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key] && filters[key] !== 'all') activeFilters[key] = filters[key];
    });
    fetchExpenses(activeFilters);
  };

  const handleFilterReset = () => {
    const resetFilters = { category: 'all', submitted_by: '', status: 'all', start_date: '', end_date: '' };
    setFilters(resetFilters);
    setActiveTab('All');
    fetchExpenses(resetFilters);
  };

  // Get attachment file from R2 (decrypted)
  const handleGetFile = async (expenseId, displayName) => {
    setAttachmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/expenses/${expenseId}/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      toast.success('File opened successfully');
    } catch (error) {
      console.error('Get file error:', error);
      if (error.response?.status === 404) {
        toast.error('No attachment found');
      } else {
        toast.error('Failed to retrieve file');
      }
    } finally {
      setAttachmentLoading(false);
    }
  };

  // Upload attachment for expense
  const handleUploadAttachment = async (expenseId, files) => {
    if (!files || files.length === 0) return;
    
    setAttachmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axios.post(`${API}/expenses/${expenseId}/attachment`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`Uploaded ${response.data.file_count} file(s) successfully`);
      fetchExpenses(); // Refresh to show attachment info
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Failed to upload attachment';
      toast.error(message);
    } finally {
      setAttachmentLoading(false);
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (expenseId) => {
    setAttachmentLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/expenses/${expenseId}/attachment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Attachment deleted');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete attachment');
    } finally {
      setAttachmentLoading(false);
    }
  };

  const handleDelete = (expenseId) => {
    setDeleteConfirm({ open: true, id: expenseId });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected expense(s)?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/expenses/bulk-delete`, { expense_ids: [...selectedIds] }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${selectedIds.size} expenses`);
      setSelectedIds(new Set());
      setSelectMode(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expenses');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === expenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(expenses.map(e => e.id)));
    }
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/expenses/${deleteConfirm.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Expense deleted successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm({ open: false, id: null });
    }
  };

  const handleStatusUpdate = async (expenseId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/expenses/${expenseId}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated successfully');
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setShowEditDialog(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 animate-fade-in">
            <div>
              <div className="w-16 h-4 rounded skeleton mb-2" />
              <div className="w-40 h-8 rounded skeleton mb-2" />
              <div className="w-32 h-4 rounded skeleton" />
            </div>
            <div className="flex gap-2">
              <div className="w-24 h-9 rounded-xl skeleton" />
              <div className="w-24 h-9 rounded-xl skeleton" />
              <div className="w-24 h-9 rounded-xl skeleton" />
            </div>
          </div>
          
          {/* Expense Cards Skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Card key={i} className="p-4 rounded-2xl border-0 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-20 h-5 rounded skeleton" />
                      <div className="w-24 h-5 rounded skeleton" />
                    </div>
                    <div className="w-32 h-4 rounded skeleton" />
                    <div className="w-48 h-3 rounded skeleton" />
                  </div>
                  <div className="text-right space-y-2">
                    <div className="w-28 h-6 rounded skeleton" />
                    <div className="w-20 h-4 rounded skeleton" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-5" data-testid="expense-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-amber-600 text-xs sm:text-sm font-bold tracking-[0.15em] uppercase mb-1">Finance</p>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Expenses
            </h1>
            <p className="text-slate-400 text-sm mt-0.5">{totalExpenses} expense{totalExpenses !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="export-csv-button"
              onClick={() => {
                if (expenses.length === 0) {
                  toast.error('No expenses to export');
                  return;
                }
                const filename = `expenses_${getTodayString()}`;
                exportToCSV(expenses, EXPENSE_COLUMNS, filename);
                toast.success(`Exported ${expenses.length} expenses to CSV`);
              }}
              variant="outline"
              className="rounded-xl h-9 w-9 p-0"
              size="sm"
              title="Export CSV"
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              data-testid="toggle-filters-button"
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className={`rounded-xl h-9 w-9 p-0 ${showFilters ? 'border-blue-500 text-blue-600 bg-blue-50' : ''}`}
              size="sm"
              title="Filter"
            >
              <Filter className="w-4 h-4" />
            </Button>
            {(filters.category !== 'all' || filters.submitted_by || filters.status !== 'all' || filters.start_date || filters.end_date) && (
              <Button
                data-testid="clear-filters-button"
                onClick={() => {
                  handleFilterReset();
                  setActiveTab('All');
                  setShowFilters(false);
                }}
                variant="outline"
                className="rounded-xl h-9 w-9 p-0 border-red-300 text-red-600 hover:bg-red-50"
                size="sm"
                title="Clear Filters"
              >
                <FilterX className="w-4 h-4" />
              </Button>
            )}
            <Button
              data-testid="add-expense-btn"
              onClick={() => navigate('/expenses/new')}
              className="h-9 px-3 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/30"
              size="sm"
            >
              <Plus className="w-4 h-4" />
              <Receipt className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="relative" ref={moreDropdownRef}>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {TAB_CATEGORIES.map((tab) => (
              <button
                key={tab}
                data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => handleTabChange(tab)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  activeTab === tab
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300 hover:text-slate-700'
                }`}
              >
                {tab === 'Transport' ? 'Transport' : tab}
              </button>
            ))}
            {/* More button scrolls with tabs */}
            <button
              data-testid="more-categories-btn"
              onClick={() => setShowMoreDropdown(!showMoreDropdown)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border border-dashed transition-all flex items-center gap-1 flex-shrink-0 ${
                MORE_CATEGORIES.includes(activeTab)
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-slate-50 text-slate-500 border-slate-300 hover:bg-slate-100'
              }`}
            >
              {MORE_CATEGORIES.includes(activeTab) ? activeTab : 'More'}
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          {/* Dropdown rendered outside scroll container so it's never clipped */}
          {showMoreDropdown && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 min-w-[200px] z-50">
              {MORE_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  data-testid={`more-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleTabChange(cat)}
                  className={`w-full text-left px-3.5 py-2 text-xs font-medium transition-colors ${
                    activeTab === cat ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <Card className="p-5 border-0 rounded-2xl shadow-lg bg-white" data-testid="filters-card">
            <h3 className="text-sm font-bold text-slate-900 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>Advanced Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger className="h-10 rounded-xl text-sm"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500">Submitted By</Label>
                <Input placeholder="Name" value={filters.submitted_by} onChange={(e) => setFilters({ ...filters, submitted_by: e.target.value })} className="h-10 rounded-xl text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500">From</Label>
                <CustomDatePicker
                  value={filters.start_date}
                  onChange={(date) => setFilters({ ...filters, start_date: date || '' })}
                  max={filters.end_date || undefined}
                  placeholder="Start date"
                  allowFuture={true}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-500">To</Label>
                <CustomDatePicker
                  value={filters.end_date}
                  onChange={(date) => setFilters({ ...filters, end_date: date || '' })}
                  min={filters.start_date || undefined}
                  placeholder="End date"
                  allowFuture={true}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleFilterApply} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs px-4">Apply</Button>
              <Button onClick={() => { handleFilterReset(); setActiveTab('All'); }} variant="outline" size="sm" className="rounded-xl text-xs px-4">Reset</Button>
            </div>
          </Card>
        )}

        {/* Edit Dialog */}
        <EditExpenseDialog
          open={showEditDialog}
          onOpenChange={(open) => {
            setShowEditDialog(open);
            if (!open) setEditingExpense(null);
          }}
          expense={editingExpense}
          users={users}
          employees={employees}
          isApprover={isApprover}
          onSaved={() => {
            setEditingExpense(null);
            fetchExpenses();
          }}
        />

        {/* Search Bar + Multi-Select Controls */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search by expense ID, vendor, description..."
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value;
                setSearchInput(val);
                if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
                const timer = setTimeout(() => {
                  setSearchQuery(val);
                  setCurrentPage(1);
                }, 300);
                setSearchDebounceTimer(timer);
              }}
              className="w-full h-10 pl-10 pr-10 rounded-xl border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 transition-colors"
              data-testid="expense-search-input"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="m21 21-4.35-4.35"/></svg>
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1); if (searchDebounceTimer) clearTimeout(searchDebounceTimer); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="expense-search-clear"
              >
                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" d="M6 18 18 6M6 6l12 12"/></svg>
              </button>
            )}
          </div>
          {isSuperUser && (
            <button
              onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
              className={`h-10 px-3 rounded-xl text-xs font-semibold border transition-colors whitespace-nowrap ${selectMode ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
              data-testid="expense-select-mode-btn"
            >
              {selectMode ? 'Cancel' : 'Select'}
            </button>
          )}
        </div>

        {/* Bulk delete bar */}
        {selectMode && (
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 py-2">
            <div className="flex items-center gap-3">
              <button onClick={toggleSelectAll} className="text-xs font-semibold text-red-700 hover:underline" data-testid="expense-select-all">
                {selectedIds.size === expenses.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-xs text-red-600">{selectedIds.size} selected</span>
            </div>
            <button
              onClick={handleBulkDelete}
              disabled={selectedIds.size === 0}
              className="h-8 px-4 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="expense-bulk-delete-btn"
            >
              Delete Selected
            </button>
          </div>
        )}

        {/* Expense Cards — Silica Loads Style */}
        {expenses.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Expenses Found</h3>
            <p className="text-slate-500 mb-4">Start by creating your first expense record</p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="sm:hidden space-y-2.5">
              {expenses.map((expense) => {
                const statusColor = expense.status === 'approved' ? 'emerald' : expense.status === 'rejected' ? 'red' : 'amber';
                return (
                  <Card
                    key={expense.id}
                    data-testid={`expense-item-${expense.id}`}
                    className={`px-4 py-3 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden bg-white border-t border-x border-slate-200 border-b-[3px] border-b-${statusColor}-500`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      {selectMode && (
                        <input type="checkbox" checked={selectedIds.has(expense.id)} onChange={() => toggleSelect(expense.id)} className="mt-1 rounded" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-slate-900 truncate">{expense.id?.startsWith('EX-') ? expense.id.toUpperCase() : (expense.vendor_name || '-')}</h3>
                          <StatusBadge status={expense.status} />
                        </div>
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold mt-1 inline-block">{expense.category === 'Employee Expenses' && expense.sub_category ? `${expense.category} - ${expense.sub_category}` : expense.category}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0" onClick={() => { setViewingExpense(expense); setShowViewDialog(true); }} data-testid={`view-expense-${expense.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0" onClick={() => openEditDialog(expense)} data-testid={`edit-expense-${expense.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {isSuperUser && (
                          <Button variant="outline" size="sm" className="rounded-xl h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(expense.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-x-3 mt-2 text-xs">
                      <div>
                        <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Amount</span>
                        <p className={`font-bold text-${statusColor}-600`}>₹{expense.amount?.toLocaleString('en-IN')}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Date</span>
                        <p className="font-medium text-slate-700">{formatDate(expense.date)}</p>
                      </div>
                      <div>
                        <span className="text-slate-400 block" style={{ fontSize: '10px' }}>Vendor</span>
                        <p className="font-medium text-slate-700 truncate">{expense.vendor_name || expense.notes || '-'}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Desktop Cards — Two-column split like Silica Loads */}
            <div className="hidden sm:grid sm:grid-cols-2 gap-3">
              {expenses.map((expense) => {
                const isApproved = expense.status === 'approved';
                const isRejected = expense.status === 'rejected';
                const edgeColor = isApproved ? 'bg-emerald-500' : isRejected ? 'bg-red-500' : 'bg-amber-400';
                const amountColor = isApproved ? 'text-emerald-600' : isRejected ? 'text-red-600' : 'text-amber-600';

                return (
                  <div
                    key={expense.id}
                    data-testid={`expense-item-${expense.id}`}
                    className="flex bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    {/* Colored left edge */}
                    <div className={`w-1.5 flex-shrink-0 ${edgeColor}`} />

                    {/* Content */}
                    <div className="flex-1 min-w-0 p-3.5 flex items-center gap-3">
                      {selectMode && (
                        <input type="checkbox" checked={selectedIds.has(expense.id)} onChange={() => toggleSelect(expense.id)} className="rounded flex-shrink-0" />
                      )}
                      {/* Left: vendor + category + date */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0">
                          <h3 className="font-bold text-slate-900 text-sm truncate min-w-0" style={{ fontFamily: "'Manrope', sans-serif" }}>
                            {expense.id?.startsWith('EX-') ? expense.id.toUpperCase() : (expense.vendor_name || '-')}
                          </h3>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap flex-shrink-0 ${
                            isApproved ? 'bg-emerald-50 text-emerald-700' : isRejected ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
                          }`}>
                            {expense.status}
                          </span>
                        </div>
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold mt-1 inline-block max-w-full truncate align-middle">{expense.category === 'Employee Expenses' && expense.sub_category ? `${expense.category} - ${expense.sub_category}` : expense.category}</span>
                        <div className="flex gap-3 mt-1.5 text-xs min-w-0">
                          <span className="text-slate-400 whitespace-nowrap flex-shrink-0">{formatDate(expense.date)}</span>
                          <span className="text-slate-400 truncate min-w-0 flex-1">{expense.vendor_name || expense.notes || '-'}</span>
                          <span className="text-slate-400 truncate min-w-0 flex-shrink">By: {expense.submitted_by}</span>
                        </div>
                      </div>

                      {/* Right: amount */}
                      <div className="text-right flex-shrink-0 pl-3 border-l border-slate-100 whitespace-nowrap">
                        <p className={`text-sm font-extrabold ${amountColor}`}>₹{expense.amount?.toLocaleString('en-IN')}</p>
                      </div>

                      {/* Action buttons - horizontal row on desktop */}
                      <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                        <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0" onClick={() => { setViewingExpense(expense); setShowViewDialog(true); }} data-testid={`view-expense-${expense.id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0" onClick={() => openEditDialog(expense)} data-testid={`edit-expense-${expense.id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {isSuperUser && (
                          <Button variant="outline" size="sm" className="rounded-lg h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(expense.id)} data-testid={`delete-expense-${expense.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Expense"
        message="Are you sure you want to delete this expense? This action cannot be undone."
        loading={deleteLoading}
      />

      {/* View Expense Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">View Expense</DialogTitle>
          </DialogHeader>
          {viewingExpense && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Amount</Label>
                  <p className="font-bold text-2xl text-slate-900">₹{viewingExpense.amount?.toLocaleString('en-IN')}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Status</Label>
                  <StatusBadge status={viewingExpense.status} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 min-w-0 overflow-hidden">
                  <Label className="text-xs text-slate-500">Date</Label>
                  <p className="font-medium text-slate-700 text-sm truncate">{formatDate(viewingExpense.date)}</p>
                </div>
                <div className="space-y-1 min-w-0 overflow-hidden">
                  <Label className="text-xs text-slate-500">Category</Label>
                  <p className="font-medium text-slate-700 truncate text-sm">{viewingExpense.category}</p>
                </div>
              </div>
              
              {viewingExpense.sub_category && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Sub Category</Label>
                  <p className="font-medium text-slate-700">{viewingExpense.sub_category}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Vendor</Label>
                  <p className="font-medium text-slate-700">{viewingExpense.vendor_name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Payment Method</Label>
                  <p className="font-medium text-slate-700">{viewingExpense.payment_method}</p>
                </div>
              </div>
              
              {/* GST Filing */}
              {viewingExpense.gst_filing && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">GST Filing</Label>
                    <p className={`font-medium ${viewingExpense.gst_filing === 'Yes' ? 'text-emerald-600' : 'text-slate-600'}`}>
                      {viewingExpense.gst_filing}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Submitted By</Label>
                <p className="font-medium text-slate-700">{viewingExpense.submitted_by}</p>
              </div>
              
              {/* Silica Purchase specific fields */}
              {viewingExpense.category === 'Silica Purchase' && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold text-slate-800 mb-3">Silica Purchase Details</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Number of Tons</Label>
                        <p className="font-medium text-slate-700">{viewingExpense.number_of_tons || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Amount per Ton</Label>
                        <p className="font-medium text-slate-700">₹{viewingExpense.amount_per_ton?.toLocaleString('en-IN') || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Offline per Ton</Label>
                        <p className="font-medium text-slate-700">₹{viewingExpense.offline_amount_per_ton?.toLocaleString('en-IN') || '-'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Offline Amount</Label>
                        <p className="font-medium text-amber-600">₹{viewingExpense.offline_amount?.toLocaleString('en-IN') || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Online Amount</Label>
                        <p className="font-medium text-blue-600">₹{viewingExpense.online_amount?.toLocaleString('en-IN') || '-'}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">GST %</Label>
                        <p className="font-medium text-slate-700">{viewingExpense.gst_percentage || '0'}%</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Delivered Tons</Label>
                        <p className="font-medium text-emerald-600">{viewingExpense.delivered_tons || '-'}</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Pending Tons</Label>
                        <p className="font-medium text-red-600">{viewingExpense.pending_tons || '-'}</p>
                      </div>
                    </div>
                    {viewingExpense.amount_per_ton_with_gst && (
                      <div className="mt-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div className="flex justify-between items-center">
                          <Label className="text-xs text-emerald-700 font-semibold">Amount per Ton (incl. GST)</Label>
                          <p className="font-bold text-emerald-700">₹{parseFloat(viewingExpense.amount_per_ton_with_gst).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Transport From Mine to Plant specific fields */}
              {viewingExpense.category === 'Transport From Mine to Plant' && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-slate-800 mb-3">Transport Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Charge per Ton</Label>
                      <p className="font-medium text-slate-700">₹{viewingExpense.charge_per_ton?.toLocaleString('en-IN') || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Total Tons</Label>
                      <p className="font-medium text-slate-700">{viewingExpense.total_tons || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Total Distance (km)</Label>
                      <p className="font-medium text-slate-700">{viewingExpense.total_distance || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Infra Development specific fields */}
              {viewingExpense.category === 'Infra Development' && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-semibold text-slate-800 mb-3">Infrastructure Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Infra Type</Label>
                      <p className="font-medium text-slate-700 capitalize">{viewingExpense.infra_type || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Infra Reason</Label>
                      <p className="font-medium text-slate-700">{viewingExpense.infra_reason || viewingExpense.infra_description || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {viewingExpense.notes && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <p className="font-medium text-slate-700">{viewingExpense.notes}</p>
                </div>
              )}
              
              {/* GST Bills Section */}
              {(viewingExpense.gst_bill_filenames?.length > 0 || viewingExpense.gst_bill_r2_keys?.length > 0) && (
                <div className="border-t pt-4">
                  <div className="bg-emerald-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-900">
                        GST Bills ({viewingExpense.gst_bill_file_count || viewingExpense.gst_bill_filenames?.length || 1})
                      </span>
                    </div>
                    {viewingExpense.gst_bill_filenames && (
                      <div className="space-y-2">
                        {viewingExpense.gst_bill_filenames.map((filename, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <p className="text-sm text-emerald-700">• {filename}</p>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await axios.get(`${API}/expenses/${viewingExpense.id}/gst-bills/${idx}`, {
                                      headers: { Authorization: `Bearer ${token}` },
                                      responseType: 'blob'
                                    });
                                    const url = URL.createObjectURL(response.data);
                                    window.open(url, '_blank');
                                  } catch (error) {
                                    toast.error('Failed to view file');
                                  }
                                }}
                              >
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await axios.get(`${API}/expenses/${viewingExpense.id}/gst-bills/${idx}`, {
                                      headers: { Authorization: `Bearer ${token}` },
                                      responseType: 'blob'
                                    });
                                    const url = URL.createObjectURL(response.data);
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.download = filename;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    URL.revokeObjectURL(url);
                                  } catch (error) {
                                    toast.error('Failed to download file');
                                  }
                                }}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Non-GST Bills/Receipts - same style as GST bills */}
              {(viewingExpense.bill_filename || viewingExpense.bill_filenames?.length > 0) && (
                <div className="border-t pt-4">
                  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">
                        Non-GST Bills/Receipts ({viewingExpense.bill_filenames?.length || 1})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {(viewingExpense.bill_filenames?.length > 0 ? viewingExpense.bill_filenames : [viewingExpense.bill_filename]).map((filename, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <p className="text-sm text-blue-700">• {filename}</p>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const endpoint = viewingExpense.bill_filenames?.length > 0 
                                    ? `${API}/expenses/${viewingExpense.id}/non-gst-bills/${idx}`
                                    : `${API}/expenses/${viewingExpense.id}/non-gst-bill`;
                                  const response = await axios.get(endpoint, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = URL.createObjectURL(response.data);
                                  window.open(url, '_blank');
                                } catch (error) {
                                  toast.error('Failed to view file');
                                }
                              }}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('token');
                                  const endpoint = viewingExpense.bill_filenames?.length > 0 
                                    ? `${API}/expenses/${viewingExpense.id}/non-gst-bills/${idx}`
                                    : `${API}/expenses/${viewingExpense.id}/non-gst-bill`;
                                  const response = await axios.get(endpoint, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = URL.createObjectURL(response.data);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  URL.revokeObjectURL(url);
                                } catch (error) {
                                  toast.error('Failed to download file');
                                }
                              }}
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* R2 Cloud Attachment */}
              {viewingExpense.attachment && (
                <div className="border-t pt-4">
                  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Attachment</span>
                    </div>
                    <p className="text-sm text-blue-700">{viewingExpense.attachment.display_name}</p>
                    {viewingExpense.attachment.file_count > 1 && (
                      <p className="text-xs text-blue-600">
                        {viewingExpense.attachment.file_count} files merged into one PDF
                      </p>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full rounded-xl bg-white hover:bg-blue-100" 
                      onClick={() => handleGetFile(viewingExpense.id, viewingExpense.attachment.display_name)}
                      disabled={attachmentLoading}
                    >
                      {attachmentLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Get File
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowViewDialog(false)} className="flex-1 h-11 rounded-xl">
                  Close
                </Button>
                <Button onClick={() => { setShowViewDialog(false); openEditDialog(viewingExpense); }} className="flex-1 h-11 rounded-xl bg-slate-900 hover:bg-slate-800">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Pagination Controls - Using standard component */}
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        totalItems={totalExpenses}
        pageSize={pageSize}
      />
    </Layout>
  );
}
